import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { whiteLabelService } from '../../services/whiteLabelService';
import { formsService } from '../../services/formsService';
import { submissionsService } from '../../services/submissionsService';
import { useAuth } from '../../contexts/AuthContext';
import PortalLoginPage from '../white-label-customer-portal/components/PortalLoginPage';
import PortalLayout from '../white-label-customer-portal/components/PortalLayout';

// ─── Defaults ─────────────────────────────────────────────────────────────────
const defaultBranding = {
  agencyName: 'Your Agency',
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '#107570',
  emailFooterText: '',
  customDomain: '',
  isEnabled: false,
};

const defaultEmbedStyle = {
  bgColor: '#ffffff',
  borderColor: '#e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  fontFamily: 'DM Sans',
  fontSize: '14px',
};

const defaultCustomerConfig = {
  portalTitle: '',
  supportEmail: '',
  privacyUrl: '/privacy',
  termsUrl: '/terms',
  showPoweredBy: true,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr)?.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

// Resolve raw stored option values to their display labels.
// Options may be plain strings or { value, label } objects.
const resolveOptionLabels = (values, options = []) =>
  values?.map((v) => {
    const opt = options?.find((o) =>
      typeof o === 'object' ? o?.value === v || o?.label === v : o === v
    );
    if (opt && typeof opt === 'object') return opt?.label ?? String(v);
    return String(v);
  });

const getSubmissionPreview = (submissionData, formElements) => {
  if (!submissionData || typeof submissionData !== 'object') return '—';
  const entries = Object.entries(submissionData)?.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries?.length === 0) return '—';
  // Prefer a non-array entry first, fall back to array entries
  const nonArrayEntry = entries?.find(([, v]) => typeof v !== 'object' || Array.isArray(v));
  const [key, value] = nonArrayEntry || entries[0];
  let preview;
  const field = formElements?.find((el) => el?.name === key || el?.id === key);
  if (field?.type === 'toggle') {
    const isOn = value === true || value === 'true';
    preview = isOn ? (field.onLabel || 'On') : (field.offLabel || 'Off');
  } else if (field?.type === 'slider') {
    const unit = field.sliderUnit || '';
    preview = unit ? `${value} ${unit}` : String(value);
  } else if (Array.isArray(value) && (field?.type === 'checkbox' || field?.type === 'multiselect' || field?.type === 'multi-select')) {
    preview = resolveOptionLabels(value, field?.options)?.join(', ') || '—';
  } else if (Array.isArray(value)) {
    preview = value?.join(', ') || '—';
  } else {
    preview = String(value);
  }
  return preview?.length > 60 ? preview?.slice(0, 60) + '…' : preview;
};

// ─── StatsCard ────────────────────────────────────────────────────────────────
const StatsCard = ({ label, value, icon, primaryColor, sub }) => (
  <div className="rounded-xl p-5 border bg-white" style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
    <div className="flex items-start justify-between mb-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
        <Icon name={icon} size={17} color={primaryColor} />
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-900 mb-0.5">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// ─── Field value formatter ────────────────────────────────────────────────────
const formatFieldValue = (key, value, formElements) => {
  const field = formElements?.find((el) => el?.name === key || el?.id === key);
  if (field) {
    if (field.type === 'toggle') {
      const isOn = value === true || value === 'true';
      return isOn ? (field.onLabel || 'On') : (field.offLabel || 'Off');
    }
    if (field.type === 'slider') {
      const unit = field.sliderUnit || '';
      return unit ? `${value} ${unit}` : String(value);
    }
    if (Array.isArray(value) && (field.type === 'checkbox' || field.type === 'multiselect' || field.type === 'multi-select')) {
      return resolveOptionLabels(value, field?.options)?.join(', ') || '—';
    }
  }
  const isFileMeta = (v) => v && typeof v === 'object' && v._fileUpload === true;
  if (isFileMeta(value)) {
    return `📎 ${value?.name || 'Attached file'}${value?.size != null ? ` (${(value.size / 1024).toFixed(1)} KB)` : ''}`;
  }
  if (Array.isArray(value) && value.length > 0 && value.every(isFileMeta)) {
    return value.map(f => `📎 ${f?.name || 'Attached file'}`).join(', ');
  }
  if (Array.isArray(value)) return value?.join(', ') || '—';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

// ─── SubmissionDetailModal ────────────────────────────────────────────────────
const SubmissionDetailModal = ({ submission, formName, formElements, branding, embedStyle, onClose }) => {
  const primaryColor = branding?.primaryColor || '#107570';
  const radius = embedStyle?.borderRadius || '8px';
  if (!submission) return null;
  const data = submission?.submissionData || submission?.submission_data || {};
  const entries = Object.entries(data)?.filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-white" style={{ borderRadius: radius, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div className="h-1 w-full rounded-t-[inherit]" style={{ backgroundColor: primaryColor }} />
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#e2e8f0' }}>
          <div>
            <h2 className="font-semibold text-gray-900 text-base">Submission Details</h2>
            <p className="text-xs text-gray-500 mt-0.5">{formName} · {formatDateTime(submission?.createdAt || submission?.created_at)}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3">
          {entries?.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No field data available.</p>
          ) : (
            entries?.map(([key, value]) => (
              <div key={key} className="rounded-lg p-3 border" style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}>
                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">{key?.replace(/_/g, ' ')}</p>
                <p className="text-sm text-gray-800 break-words">{formatFieldValue(key, value, formElements)}</p>
              </div>
            ))
          )}
        </div>
        <div className="px-6 py-3 border-t flex items-center justify-between" style={{ borderColor: '#e2e8f0' }}>
          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
            {submission?.status || 'new'}
          </span>
          <p className="text-xs text-gray-400">ID: {(submission?.id || '')?.slice(0, 8)}…</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const PortalSubmissionsDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [branding, setBranding] = useState(defaultBranding);
  const [embedStyle] = useState(defaultEmbedStyle);
  const [customerConfig, setCustomerConfig] = useState(defaultCustomerConfig);
  const [forms, setForms] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [selectedFormId, setSelectedFormId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Detail modal
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const primaryColor = branding?.primaryColor || '#107570';
  const radius = embedStyle?.borderRadius || '8px';

  // ── Load data ──
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      try {
        const [wlSettings, formsData, submissionsData] = await Promise.all([
          whiteLabelService?.getSettings()?.catch(() => null),
          formsService?.getForms()?.catch(() => []),
          submissionsService?.getAllSubmissions()?.catch(() => []),
        ]);

        if (wlSettings?.customerConfig && Object.keys(wlSettings.customerConfig)?.length > 0) {
          setCustomerConfig((prev) => ({ ...prev, ...wlSettings.customerConfig }));
        }
        if (wlSettings) {
          setBranding({
            agencyName: wlSettings?.agencyName || 'Your Agency',
            logoUrl: wlSettings?.logoUrl || '',
            faviconUrl: wlSettings?.faviconUrl || '',
            primaryColor: wlSettings?.primaryColor || '#107570',
            emailFooterText: wlSettings?.emailFooterText || '',
            customDomain: wlSettings?.customDomain || '',
            isEnabled: wlSettings?.isEnabled || false,
          });
        }

        setForms(formsData || []);
        setAllSubmissions(submissionsData || []);
      } catch (err) {
        console.error('Portal submissions load error:', err);
        setError('Unable to load submissions. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  // ── Helpers ──
  const getFormName = useCallback(
    (formId) => forms?.find((f) => f?.id === formId)?.name || 'Unknown Form',
    [forms]
  );

  // ── Filtered submissions ──
  const filteredSubmissions = allSubmissions?.filter((sub) => {
    const formId = sub?.formId || sub?.form_id;
    const createdAt = sub?.createdAt || sub?.created_at;

    if (selectedFormId !== 'all' && formId !== selectedFormId) return false;

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      if (new Date(createdAt) < from) return false;
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to?.setHours(23, 59, 59, 999);
      if (new Date(createdAt) > to) return false;
    }

    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      const dataStr = JSON.stringify(sub?.submissionData || sub?.submission_data || {})?.toLowerCase();
      const formName = getFormName(formId)?.toLowerCase();
      if (!dataStr?.includes(q) && !formName?.includes(q)) return false;
    }

    return true;
  });

  // ── Stats ──
  const totalSubmissions = allSubmissions?.length || 0;
  const todayCount = allSubmissions?.filter((s) => {
    const d = new Date(s?.createdAt || s?.created_at);
    const now = new Date();
    return d?.toDateString() === now?.toDateString();
  })?.length || 0;
  const uniqueForms = new Set(allSubmissions?.map((s) => s?.formId || s?.form_id))?.size;

  // ── Export CSV ──
  const handleExport = () => {
    if (filteredSubmissions?.length === 0) return;

    const allKeys = new Set();
    filteredSubmissions?.forEach((s) => {
      const data = s?.submissionData || s?.submission_data || {};
      Object.keys(data)?.forEach((k) => allKeys?.add(k));
    });

    const headers = ['Form', 'Submitted At', 'Status', ...Array.from(allKeys)];
    const rows = filteredSubmissions?.map((s) => {
      const data = s?.submissionData || s?.submission_data || {};
      const formId = s?.formId || s?.form_id;
      const formObj = forms?.find((f) => f?.id === formId);
      const formElements = formObj?.elements || formObj?.formElements || [];
      return [
        getFormName(formId),
        formatDateTime(s?.createdAt || s?.created_at),
        s?.status || 'new',
        ...Array.from(allKeys)?.map((k) => {
          const v = data?.[k];
          if (v === null || v === undefined) return '';
          const formatted = formatFieldValue(k, v, formElements);
          return formatted?.replace(/"/g, '""');
        }),
      ];
    });

    const csvContent = [headers, ...rows]?.map((row) => row?.map((cell) => `"${cell}"`)?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `submissions-${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSelectedFormId('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedFormId !== 'all' || dateFrom || dateTo || searchQuery;

  const portalTitle = customerConfig?.portalTitle || branding?.agencyName
    ? `${customerConfig?.portalTitle || branding?.agencyName} — Submissions`
    : 'Submissions Dashboard';

  // ── Auth loading ──
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${primaryColor}40`, borderTopColor: primaryColor }} />
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  // ── Not authenticated ──
  if (!user) {
    return (
      <>
        <Helmet>
          <title>{portalTitle}</title>
          {branding?.faviconUrl && <link rel="icon" href={branding?.faviconUrl} />}
        </Helmet>
        <PortalLoginPage
          branding={branding}
          embedStyle={embedStyle}
          customerConfig={customerConfig}
          onLoginSuccess={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{portalTitle}</title>
        {branding?.faviconUrl && <link rel="icon" href={branding?.faviconUrl} />}
      </Helmet>

      <PortalLayout branding={branding} customerConfig={customerConfig} pageTitle="Submissions">
        {/* ── Page Header ── */}
        <div
          className="border-b px-4 sm:px-6 lg:px-8 py-8"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}04 100%)`,
            borderColor: `${primaryColor}20`,
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 leading-tight">Form Submissions</h1>
                <p className="text-sm text-gray-500">View, filter, and export responses across all your forms.</p>
              </div>
              <button
                type="button"
                onClick={handleExport}
                disabled={filteredSubmissions?.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed self-start sm:self-auto"
                style={{ backgroundColor: primaryColor, borderRadius: radius }}
              >
                <Icon name="Download" size={15} />
                Export CSV
                {filteredSubmissions?.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}>
                    {filteredSubmissions?.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${primaryColor}40`, borderTopColor: primaryColor }} />
                <p className="text-sm text-gray-500">Loading submissions…</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <Icon name="AlertCircle" size={22} color="#ef4444" />
              </div>
              <p className="text-gray-700 font-medium mb-1">Something went wrong</p>
              <p className="text-sm text-gray-500">{error}</p>
            </div>
          ) : (
            <>
              {/* ── Stats ── */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-7">
                <StatsCard label="Total Submissions" value={totalSubmissions?.toLocaleString()} icon="Inbox" primaryColor={primaryColor} sub="all time" />
                <StatsCard label="Today" value={todayCount?.toLocaleString()} icon="CalendarCheck" primaryColor={primaryColor} sub="new responses" />
                <StatsCard label="Forms with Responses" value={uniqueForms?.toLocaleString()} icon="FileText" primaryColor={primaryColor} sub={`of ${forms?.length} total`} />
              </div>

              {/* ── Filters ── */}
              <div className="rounded-xl border bg-white p-4 mb-5" style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Icon name="Search" size={14} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search responses…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none transition-all"
                      style={{ borderColor: '#e2e8f0', borderRadius: '8px' }}
                      onFocus={(e) => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>

                  {/* Form filter */}
                  <div className="relative sm:w-52">
                    <Icon name="FileText" size={14} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <select
                      value={selectedFormId}
                      onChange={(e) => setSelectedFormId(e?.target?.value)}
                      className="w-full pl-8 pr-8 py-2 text-sm border rounded-lg text-gray-700 bg-white focus:outline-none appearance-none transition-all"
                      style={{ borderColor: '#e2e8f0', borderRadius: '8px' }}
                      onFocus={(e) => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                    >
                      <option value="all">All Forms</option>
                      {forms?.map((f) => (
                        <option key={f?.id} value={f?.id}>{f?.name || 'Untitled Form'}</option>
                      ))}
                    </select>
                    <Icon name="ChevronDown" size={13} color="#9ca3af" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  {/* Date from */}
                  <div className="relative sm:w-40">
                    <Icon name="Calendar" size={14} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e?.target?.value)}
                      className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg text-gray-700 bg-white focus:outline-none transition-all"
                      style={{ borderColor: '#e2e8f0', borderRadius: '8px' }}
                      onFocus={(e) => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                      title="From date"
                    />
                  </div>

                  {/* Date to */}
                  <div className="relative sm:w-40">
                    <Icon name="Calendar" size={14} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e?.target?.value)}
                      className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg text-gray-700 bg-white focus:outline-none transition-all"
                      style={{ borderColor: '#e2e8f0', borderRadius: '8px' }}
                      onFocus={(e) => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                      title="To date"
                    />
                  </div>

                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border text-gray-500 hover:bg-gray-50 transition-colors whitespace-nowrap"
                      style={{ borderColor: '#e2e8f0', borderRadius: '8px' }}
                    >
                      <Icon name="X" size={13} />
                      Clear
                    </button>
                  )}
                </div>

                {/* Active filter chips */}
                {hasActiveFilters && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#f1f5f9' }}>
                    {selectedFormId !== 'all' && (
                      <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}>
                        <Icon name="FileText" size={11} />
                        {getFormName(selectedFormId)}
                        <button type="button" onClick={() => setSelectedFormId('all')} className="ml-0.5 hover:opacity-70"><Icon name="X" size={10} /></button>
                      </span>
                    )}
                    {dateFrom && (
                      <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}>
                        From {formatDate(dateFrom)}
                        <button type="button" onClick={() => setDateFrom('')} className="ml-0.5 hover:opacity-70"><Icon name="X" size={10} /></button>
                      </span>
                    )}
                    {dateTo && (
                      <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}>
                        To {formatDate(dateTo)}
                        <button type="button" onClick={() => setDateTo('')} className="ml-0.5 hover:opacity-70"><Icon name="X" size={10} /></button>
                      </span>
                    )}
                    {searchQuery && (
                      <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}>
                        "{searchQuery}"
                        <button type="button" onClick={() => setSearchQuery('')} className="ml-0.5 hover:opacity-70"><Icon name="X" size={10} /></button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* ── Table ── */}
              <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: '#f1f5f9' }}>
                  <p className="text-sm font-semibold text-gray-800">
                    {filteredSubmissions?.length > 0
                      ? `${filteredSubmissions?.length} submission${filteredSubmissions?.length !== 1 ? 's' : ''}`
                      : 'No submissions'}
                    {hasActiveFilters && <span className="text-gray-400 font-normal"> (filtered)</span>}
                  </p>
                  {filteredSubmissions?.length > 0 && (
                    <button type="button" onClick={handleExport} className="flex items-center gap-1.5 text-xs font-medium transition-colors" style={{ color: primaryColor }}>
                      <Icon name="Download" size={13} color={primaryColor} />
                      Export
                    </button>
                  )}
                </div>

                {filteredSubmissions?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}12` }}>
                      <Icon name="Inbox" size={24} color={primaryColor} />
                    </div>
                    <h3 className="text-base font-semibold text-gray-800 mb-1">
                      {hasActiveFilters ? 'No matching submissions' : 'No submissions yet'}
                    </h3>
                    <p className="text-sm text-gray-500 max-w-xs">
                      {hasActiveFilters ? 'Try adjusting your filters to see more results.' : 'Submissions will appear here once customers fill out your forms.'}
                    </p>
                    {hasActiveFilters && (
                      <button type="button" onClick={clearFilters} className="mt-4 text-xs font-medium transition-colors" style={{ color: primaryColor }}>
                        Clear all filters
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Form</th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Preview</th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Submitted</th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                          <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ borderColor: '#f8fafc' }}>
                        {filteredSubmissions?.map((sub) => {
                          const formId = sub?.formId || sub?.form_id;
                          const createdAt = sub?.createdAt || sub?.created_at;
                          return (
                            <tr key={sub?.id} className="hover:bg-gray-50/60 transition-colors cursor-pointer" onClick={() => setSelectedSubmission(sub)}>
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primaryColor}15` }}>
                                    <Icon name="FileText" size={13} color={primaryColor} />
                                  </div>
                                  <span className="font-medium text-gray-800 text-sm truncate max-w-[160px]">{getFormName(formId)}</span>
                                </div>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className="text-gray-500 text-sm truncate max-w-[200px] block">{getSubmissionPreview(sub?.submissionData || sub?.submission_data, forms?.find((f) => f?.id === formId)?.formElements || [])}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className="text-gray-500 text-sm whitespace-nowrap">{formatDateTime(createdAt)}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium capitalize" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}>
                                  {sub?.status || 'new'}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-right">
                                <button
                                  type="button"
                                  onClick={(e) => { e?.stopPropagation(); setSelectedSubmission(sub); }}
                                  className="text-xs font-medium transition-colors hover:opacity-70"
                                  style={{ color: primaryColor }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </PortalLayout>

      {/* ── Detail Modal ── */}
      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          formName={getFormName(selectedSubmission?.formId || selectedSubmission?.form_id)}
          formElements={forms?.find((f) => f?.id === (selectedSubmission?.formId || selectedSubmission?.form_id))?.formElements || []}
          branding={branding}
          embedStyle={embedStyle}
          onClose={() => setSelectedSubmission(null)}
        />
      )}
    </>
  );
};

export default PortalSubmissionsDashboard;
